import { XIcon } from 'lucide-react';
import { useState, type JSX, type KeyboardEvent } from 'react';

import { Input } from '@/components/ui/input.js';

import { sanitizeTag, TAGS_MAX } from './frontmatter.js';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
}

/**
 * Chips-style editor for a skill's tags (Jira-labels idiom). Enter or comma
 * commits the typed text — sanitized into the lowercase-kebab tag shape via
 * `sanitizeTag`, so there is no invalid-input error state to surface —
 * Backspace on an empty field removes the last chip, and each chip carries
 * its own remove button. Silently ignores entries past `TAGS_MAX` and
 * duplicates.
 */
export function TagInput({ value, onChange }: TagInputProps): JSX.Element {
  const [draft, setDraft] = useState('');

  function commitDraft(): void {
    const tag = sanitizeTag(draft);
    setDraft('');
    if (!tag || value.includes(tag) || value.length >= TAGS_MAX) return;
    onChange([...value, tag]);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitDraft();
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-xs text-foreground"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                onClick={() =>
                  onChange(value.filter((existing) => existing !== tag))
                }
                className="rounded-full text-muted-foreground transition-colors hover:text-foreground"
              >
                <XIcon className="size-3" strokeWidth={2} aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitDraft}
        placeholder={value.length === 0 ? 'ba, reporting, …' : ''}
        aria-label="Add tag"
        disabled={value.length >= TAGS_MAX}
      />
    </div>
  );
}
